import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Bell, Archive, CheckCircle2, X, Filter } from "lucide-react";

const PRIORITA_COLORS = {
  bassa: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-300",
  media: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-blue-300",
  alta: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-400",
  critica: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 border-rose-400",
};

function PrioBadge({ p }) {
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${PRIORITA_COLORS[p] || ""}`}>{p}</span>;
}

export default function Alert() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fStato, setFStato] = useState("attivo");
  const [fPrio, setFPrio] = useState("");
  const [fHotel, setFHotel] = useState("");
  const [drawer, setDrawer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fStato) params.set("stato", fStato);
      if (fPrio) params.set("priorita", fPrio);
      if (fHotel) params.set("hotel_id", fHotel);
      const [a, h] = await Promise.all([api.get(`/alerts?${params}`), api.get("/hotels")]);
      setAlerts(a.data || []);
      setHotels(h.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [fStato, fPrio, fHotel]); // eslint-disable-line

  const stats = useMemo(() => {
    const attivi = alerts.filter((a) => a.stato === "attivo");
    return {
      tot: alerts.length,
      critici_attivi: attivi.filter((a) => a.priorita === "critica").length,
      alta_attivi: attivi.filter((a) => a.priorita === "alta").length,
    };
  }, [alerts]);

  const nomeHotel = (id) => hotels.find((h) => h.id === id)?.nome || "—";

  const action = async (a, type) => {
    try {
      await api.patch(`/alerts/${a.id}/${type}`);
      toast.success(type === "risolvi" ? "Alert risolto" : "Alert archiviato");
      load();
      setDrawer(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="alert-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" /> Centro Alert</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Avvisi <span className="font-medium">operativi</span></h1>
        <p className="text-sm text-muted-foreground mt-1"><span className="text-rose-600 font-semibold">{stats.critici_attivi}</span> critici attivi · <span className="text-amber-600 font-semibold">{stats.alta_attivi}</span> alta priorità · {stats.tot} totali nel filtro</p>
      </div>

      <div className="ih-glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <select data-testid="filter-stato" value={fStato} onChange={(e) => setFStato(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutti gli stati</option>
          <option value="attivo">Attivi</option>
          <option value="risolto">Risolti</option>
          <option value="archiviato">Archiviati</option>
        </select>
        <select data-testid="filter-prio" value={fPrio} onChange={(e) => setFPrio(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutte le priorità</option>
          <option value="critica">Critica</option>
          <option value="alta">Alta</option>
          <option value="media">Media</option>
          <option value="bassa">Bassa</option>
        </select>
        <select data-testid="filter-hotel" value={fHotel} onChange={(e) => setFHotel(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutti gli hotel</option>
          {hotels.map((h) => <option key={h.id} value={h.id}>{h.nome}</option>)}
        </select>
      </div>

      <div className="ih-glass rounded-2xl p-6">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : alerts.length === 0 ? (
          <div className="ih-empty-state"><div className="ih-empty-state-icon"><svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div><h3 className="text-lg font-semibold mb-1">Nessun alert</h3><p className="text-sm text-muted-foreground max-w-md">Tutto sotto controllo. Gli alert appariranno qui quando il motore rileverà anomalie.</p></div>
        ) : (
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} onClick={() => setDrawer(a)} data-testid={`alert-${a.id}`}
                className={`rounded-xl border-l-4 border border-border/60 p-4 bg-background/40 hover:bg-accent/30 cursor-pointer flex items-center gap-3 ${PRIORITA_COLORS[a.priorita]?.split(" ").find((c) => c.startsWith("border-")) || ""}`}>
                <Bell className={`h-4 w-4 ${a.priorita === "critica" ? "text-rose-500" : a.priorita === "alta" ? "text-amber-500" : "text-muted-foreground"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium text-sm truncate">{a.titolo}</span>
                    <PrioBadge p={a.priorita} />
                    {a.stato !== "attivo" && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.stato}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{nomeHotel(a.hotel_id)} · {new Date(a.data_creazione).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <div className="text-[11px] text-muted-foreground hidden sm:block">{a.destinatari?.length || 0} dest.</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {drawer && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" onClick={() => setDrawer(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:w-[480px] h-full bg-background border-l border-border overflow-y-auto" data-testid="alert-drawer">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{drawer.trigger_type}</div>
                  <h2 className="font-display text-2xl tracking-tight">{drawer.titolo}</h2>
                </div>
                <button onClick={() => setDrawer(null)} className="h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <PrioBadge p={drawer.priorita} />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{drawer.stato}</span>
                <span className="text-xs text-muted-foreground">· {new Date(drawer.data_creazione).toLocaleString("it-IT")}</span>
              </div>
              <p className="text-sm bg-accent/30 rounded-xl p-4 mb-4">{drawer.messaggio}</p>
              {drawer.hotel_id && <div className="text-xs mb-2"><span className="text-muted-foreground">Hotel: </span><strong>{nomeHotel(drawer.hotel_id)}</strong></div>}
              {drawer.entity_type && drawer.entity_id && (
                <button onClick={() => navigate(drawer.entity_type === "audit" ? `/ispezioni/${drawer.entity_id}` : drawer.entity_type === "report" ? `/relazioni/${drawer.entity_id}` : drawer.entity_type === "rm" ? `/performance` : `/strutture/${drawer.entity_id}`)} className="text-xs text-primary hover:underline">Apri {drawer.entity_type} →</button>
              )}
              {drawer.stato === "attivo" && (
                <div className="mt-6 flex gap-2 sticky bottom-0 bg-background pt-3 border-t border-border">
                  <button onClick={() => action(drawer, "archivia")} data-testid="archivia-btn" className="flex-1 px-4 py-2 rounded-full border border-border text-sm flex items-center justify-center gap-2"><Archive className="h-4 w-4" /> Archivia</button>
                  <button onClick={() => action(drawer, "risolvi")} data-testid="risolvi-btn" className="flex-1 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4" /> Risolvi</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
